import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { trackInternalEvent } from '@rio/tracking'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as SimpleForm from 'builder/components/SimpleForm'
import { useI18n } from 'builder/hooks/useI18n'
import { queryClient } from 'builder/components/Providers/Providers'
import { AutoApplyForm } from 'builder/modules/autoApply/types'
import { selectors } from 'builder/modules/autoApply/autoApplyModule'
import { actions as careerProfileActions } from 'builder/modules/careerProfile'
import { FetchStatuses } from 'builder/modules/constants'
import { Routes, TotalPageCount } from '../../constants'
import { Container, FillWarning, LayoutContainer, Title, TitleWrapper } from '../styles'
import { ConfirmModal } from '../ConfirmModal/ConfirmModal'
import { usePatchForm } from '../../hooks/usePatchForm'
import { usePrompt } from '../../hooks/usePrompt'
import { useStepsConfig } from '../../hooks/useStepsConfig'
import { FormContainer } from './styles'
import { JobPreferencesForm2 } from './JobPreferencesForm2'

const TRANSLATION = 'builder.auto_apply.form.job_preferences_2'

export const JobPreferencesForm2Container = () => {
  const formState = useSelector(selectors.form)
  const dispatch = useDispatch()

  const isEditMode = formState.autoApplyDataCompleted
  const form = useForm<
    Pick<
      AutoApplyForm,
      'countriesAuthorizedToWorkIn' | 'targetLocations' | 'jobPreferenceAttributes'
    >
  >({
    mode: 'onChange',
  })

  const { i18n } = useI18n()
  const navigate = useNavigate()
  const stpesConfig = useStepsConfig()

  const { isPromptVisible, handleCancel, handleConfirm } = usePrompt({
    when: true,
    ignoreParentRoute: isEditMode ? ['auto-apply', 'job-search'] : ['auto-apply'],
  })

  const { mutateAsync: patchForm, isLoading } = usePatchForm()

  const onNextStep = async () => {
    const mapCountries = form.getValues('countriesAuthorizedToWorkIn').map(item => item.value)
    const data = {
      ...form.getValues(),
      countriesAuthorizedToWorkIn: mapCountries,
      autoApplyDataCompleted: true,
    }

    // @ts-expect-error countries
    await patchForm(data)
    await queryClient.invalidateQueries(['useGetCareerProfile'])
    // force redux to update career profile
    dispatch(careerProfileActions.setFetchCareerProfileStatus(FetchStatuses.notAsked))

    const path = isEditMode
      ? '/job-search?view=recommendation'
      : `/auto-apply/${stpesConfig[Routes.JOB_PREFERENCES_2].next}`

    navigate(path)
  }

  const onBackClick = () => {
    form.clearErrors()
    navigate(`/auto-apply/${stpesConfig[Routes.JOB_PREFERENCES_2].prev}`)
  }

  useEffect(() => {
    trackInternalEvent('aa_job_preferences_location', {
      label: 'total_job_search_solution',
    })
  }, [])

  useEffect(() => {
    const { countriesAuthorizedToWorkIn, jobPreferenceAttributes, targetLocations } = formState
    form.setValue('countriesAuthorizedToWorkIn', countriesAuthorizedToWorkIn)
    form.setValue('targetLocations', targetLocations)
    form.setValue('jobPreferenceAttributes', jobPreferenceAttributes)
  }, [form, formState])

  return (
    <LayoutContainer>
      <Container>
        <TitleWrapper>
          <Title>{i18n.t(`${TRANSLATION}.title`)}</Title>
          <FillWarning>{i18n.t(`${TRANSLATION}.sub_title`)}</FillWarning>
        </TitleWrapper>
        <FormContainer>
          <SimpleForm.Form
            submitButtonText={i18n.t(`${TRANSLATION}.next`)}
            onBackClick={onBackClick}
            onSubmit={form.handleSubmit(onNextStep)}
            isPending={isLoading}
            processIndicator={{
              totalStep: TotalPageCount,
              currentStep: stpesConfig[Routes.JOB_PREFERENCES_2].order,
            }}
          >
            <JobPreferencesForm2 form={form} />
          </SimpleForm.Form>
        </FormContainer>
      </Container>
      {isPromptVisible && <ConfirmModal onClose={handleCancel} onConfirm={handleConfirm} />}
    </LayoutContainer>
  )
}
